/**
 * Per-locale URL prefixes (/en, /de, /pl, /nl).
 *
 * The app is served from the same SPA bundle for every locale; the prefix is
 * consumed as the React Router `basename`, so all in-app <Link to="/x"> keep
 * the active locale automatically and deep links stay intact.
 */
export const SUPPORTED_LOCALES = ["en", "de", "pl", "nl"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const SITE_URL = "https://valorform.com";

export function isSupportedLocale(v: string | null | undefined): v is SupportedLocale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

/** Splits "/pl/services/nutrition" → { locale: "pl", path: "/services/nutrition" }. */
export function splitLocalePath(pathname: string): { locale: SupportedLocale | null; path: string } {
  const m = /^\/([a-z]{2})(\/.*)?$/i.exec(pathname || "/");
  const candidate = m?.[1]?.toLowerCase();
  if (isSupportedLocale(candidate)) {
    return { locale: candidate, path: m?.[2] || "/" };
  }
  return { locale: null, path: pathname || "/" };
}

/** Builds an absolute, locale-prefixed URL for canonical / hreflang tags. */
export function localeUrl(locale: SupportedLocale | null, path: string): string {
  const clean = path === "/" ? "" : path.replace(/\/+$/, "");
  return `${SITE_URL}${locale ? `/${locale}` : ""}${clean}` || `${SITE_URL}/`;
}

/** The locale prefix of the current browser URL, or null when unprefixed. */
export function currentLocalePrefix(): SupportedLocale | null {
  if (typeof window === "undefined") return null;
  return splitLocalePath(window.location.pathname).locale;
}
